import { ChevronDown, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from './lib/cn';

export interface SectionHeaderProps {
  title: string;
  /**
   * `section` — sidebar group header, e.g. "Teams" (13px semibold).
   * `label`   — quiet sub-label, e.g. "Pinned" / "Your teams" (11px muted caps-ish).
   * `card`    — card title, e.g. "Upcoming events" (15px semibold).
   */
  variant?: 'section' | 'label' | 'card';
  /** Makes the whole row a collapse toggle and renders a rotating chevron. */
  collapsible?: boolean;
  collapsed?: boolean;
  onToggle?: () => void;
  /** Trailing controls — "+" button, "See all" link, "..." menu. */
  action?: ReactNode;
  /** Leading glyph. */
  icon?: LucideIcon;
  /** Item count appended to the title, e.g. "People online (23)". */
  count?: number;
  className?: string;
}

const VARIANT = {
  section: 'text-base font-semibold text-foreground',
  label: 'text-xs font-medium uppercase tracking-wide text-muted-foreground',
  card: 'text-lg font-semibold text-foreground',
} as const;

/** Group heading used by the sidebar, the right rail and every card. */
export function SectionHeader({
  title, variant = 'section', collapsible = false, collapsed = false,
  onToggle, action, icon: Icon, count, className,
}: SectionHeaderProps) {
  const heading = count !== undefined ? `${title} (${count})` : title;

  return (
    <div className={cn('flex min-h-8 items-center justify-between gap-2', className)}>
      {collapsible ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!collapsed}
          className="flex min-w-0 flex-1 items-center gap-1.5 rounded-md px-1 py-1 text-left transition-colors duration-fast hover:bg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ChevronDown
            size={14}
            strokeWidth={2.5}
            aria-hidden
            className={cn(
              'shrink-0 text-muted-foreground transition-transform duration-fast',
              collapsed && '-rotate-90',
            )}
          />
          {Icon ? <Icon size={14} className="shrink-0 text-muted-foreground" aria-hidden /> : null}
          <span className={cn('truncate', VARIANT[variant])}>{heading}</span>
        </button>
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-1.5 px-1">
          {Icon ? <Icon size={14} className="shrink-0 text-muted-foreground" aria-hidden /> : null}
          <h3 className={cn('truncate', VARIANT[variant])}>{heading}</h3>
        </div>
      )}

      {action ? <div className="flex shrink-0 items-center gap-0.5">{action}</div> : null}
    </div>
  );
}
